/**
 * The realtime broadcasts the model-capability plane emits, and the identity-
 * only payloads they carry.
 *
 * Snake-case-verbatim mirrors of Backend/app/schemas/evaluation/events.py and
 * the relay plane's two frames. Global-room membership is vetoed at join time
 * rather than per payload, so every field here is an identity, a count or a
 * value from a closed vocabulary — never a rationale or a metric.
 *
 * Backend/tests/contract/test_model_capabilities_wire_parity.py pins every payload
 * below to the Python owner it mirrors, so a schema edit that forgets this file
 * fails there rather than at a user's screen.
 */

import type { ConnectionLifecycle } from './connections'
import type {
  DeploymentLifecycle,
  EvaluationRunStatus,
  InferenceJobStatus,
  JobFailureClass,
  QualificationState,
  RelayCompatibility,
} from './index'

/**
 * Every broadcast the governance and work planes emit, in the order the
 * backend declares them. Exported so the hooks' consumption constants and the
 * pin that unions them share one source of truth for the list.
 */
export const MODEL_CAPABILITY_EVENTS = [
  'model_evaluation_run_changed',
  'model_qualification_changed',
  'model_selection_policy_changed',
  'model_selection_bundle_changed',
  'model_suite_changed',
  'model_deployment_changed',
  'model_connection_changed',
  'model_catalogue_changed',
  'model_relay_changed',
  'inference_job_changed',
] as const

export type ModelCapabilityEventName = (typeof MODEL_CAPABILITY_EVENTS)[number]

/** Whether a probe found a deployment able to serve its declared window. */
export type CapacityVerdict = 'pass' | 'fail'

/** A run's claim, throttled progress, or terminal outcome. */
export interface ModelEvaluationRunChangedPayload {
  run_id: string
  bundle_run_id: string | null
  capability_key: string
  suite_id: string
  deployment_id: string
  status: EvaluationRunStatus
  case_completed: number
  case_total: number
  attempt_count: number
  metadata_version: number
}

/** An admission reviewed by an administrator, or expired by the sweep. */
export interface ModelQualificationChangedPayload {
  qualification_id: string
  capability_key: string
  deployment_id: string
  state: QualificationState
  run_id: string
  metadata_version: number
}

/** A capability's allowed set/default, or its posture/pin, changed. */
export interface ModelSelectionPolicyChangedPayload {
  policy_id: string
  capability_key: string
  project_id: string | null
  metadata_version: number
}

/** A pipeline's approved choices, or its active choice, changed. */
export interface ModelSelectionBundleChangedPayload {
  bundle_id: string
  bundle_key: string
  project_id: string | null
  bundle_version: number
  metadata_version: number
}

/**
 * A suite was registered, or its readiness and gate standing recorded.
 * Exactly one of the two keys is set, matching the row it describes.
 */
export interface ModelSuiteChangedPayload {
  suite_id: string
  capability_key: string | null
  bundle_key: string | null
  version: string
  gate_eligible: boolean
  metadata_version: number
}

/** A deployment was registered, its revision observed, or capacity-probed. */
export interface ModelDeploymentChangedPayload {
  deployment_id: string
  deployment_key: string
  lifecycle: DeploymentLifecycle
  capacity_verdict: CapacityVerdict | null
  observed_at: string | null
  metadata_version: number
}

/**
 * A provider connection was registered, edited, verified or moved.
 * `serving_changed` says whether this write altered what may actually be
 * dialled, so a client can tell a lifecycle move from an evidence stamp
 * without re-reading the row to find out.
 */
export interface ModelConnectionChangedPayload {
  connection_id: string
  connection_key: string
  lifecycle: ConnectionLifecycle
  serving_changed: boolean
  metadata_version: number
}

/** A catalogue model was created, edited or retired. */
export interface ModelCatalogueChangedPayload {
  model_id: string
  pattern: string
  retired: boolean
  metadata_version: number
}

/** A relay's registration, rebinding, liveness edge, or inventory change. */
export interface ModelRelayChangedPayload {
  relay_id: string
  installation_id: string
  host_label: string
  live: boolean
  compatible: RelayCompatibility
  image_version: string
  inventory_count: number
  spool_depth: number
  metadata_version: number
}

/** A job's queue, offer, settlement, cancel, or sweeper transition. */
export interface InferenceJobChangedPayload {
  job_id: string
  capability_key: string | null
  deployment_id: string
  status: InferenceJobStatus
  attempt_count: number
  selected_relay_id: string | null
  failure_class: JobFailureClass | null
  metadata_version: number
}

/** The payload shapes the broadcasts carry, in the tuple's own order. */
export type ModelCapabilityEventPayload =
  | ModelEvaluationRunChangedPayload
  | ModelQualificationChangedPayload
  | ModelSelectionPolicyChangedPayload
  | ModelSelectionBundleChangedPayload
  | ModelSuiteChangedPayload
  | ModelDeploymentChangedPayload
  | ModelConnectionChangedPayload
  | ModelCatalogueChangedPayload
  | ModelRelayChangedPayload
  | InferenceJobChangedPayload
